import { Pipe, PipeTransform } from '@angular/core';

import {
  LocaleService,
  TranslationKey,
  TranslationService,
  UnitConversionValue,
  UnitConverter,
  LanguageSelectionService,
  FsNumberPipe,
} from '@lib/translation';

const FEET_PER_MILE = 5280;
const METERS_PER_KILOMETER = 1000;

export interface FsMilesToDistanceOptions {
  precision?: number;
  includeUnit?: boolean;
}

const DEFAULT_OPTIONS: Required<FsMilesToDistanceOptions> = {
  precision: 0,
  includeUnit: true,
};

/**
 * Pipe that converts a distance value in miles to the most appropriate unit based on the locale.
 *
 * For imperial locales:
 * - If distance < 1 mile: converts to feet and displays "feet"
 * - If distance >= 1 mile: displays "miles"
 *
 * For metric locales:
 * - If distance < 1 kilometer: converts to meters and displays "meters"
 * - If distance >= 1 kilometer: converts to kilometers and displays "kilometers"
 *
 * @example
 * // Imperial locale examples:
 * {{ 0.5 | fsMilesToDistance }}        // "2640 feet"
 * {{ 1.5 | fsMilesToDistance }}       // "2 miles"
 * {{ 0.002 | fsMilesToDistance }}     // "11 feet"
 *
 * // With precision option:
 * {{ 0.5 | fsMilesToDistance:1 }}     // "2640.0 feet"
 * {{ 1.5 | fsMilesToDistance:2 }}   // "1.50 miles"
 *
 * // With options object:
 * {{ 0.5 | fsMilesToDistance:{precision: 0, includeUnit: true} }}  // "2640 feet"
 * {{ 1.5 | fsMilesToDistance:{precision: 1, includeUnit: false} }}  // "1.5"
 *
 * @param value - Distance value in miles (number, null, or undefined)
 * @param options - Optional configuration:
 *   - If a number: sets the precision (decimal places)
 *   - If an object: { precision?: number, includeUnit?: boolean }
 *     - precision: Number of decimal places (default: 0)
 *     - includeUnit: Whether to include the unit in output (default: true)
 * @returns Formatted string with distance and unit, or empty string if value is null/undefined
 */
@Pipe({
  name: 'fsMilesToDistance',
  pure: false,
})
export class FsMilesToDistancePipe implements PipeTransform {
  private readonly _numberPipe: FsNumberPipe;

  constructor(
    private readonly _localeService: LocaleService,
    private readonly _translationService: TranslationService,
    private readonly _languageService: LanguageSelectionService,
  ) {
    this._numberPipe = new FsNumberPipe(this._languageService);
  }

  transform(value: UnitConversionValue, options?: FsMilesToDistanceOptions | number): string {
    if (value == null) return '';

    const opts =
      typeof options === 'number'
        ? { ...DEFAULT_OPTIONS, precision: options }
        : { ...DEFAULT_OPTIONS, ...options };

    const unitConfig = this._localeService.getUnitConfig();

    // Miles & Feet
    if (unitConfig.system === 'imperial') {
      return this._formatDistance({
        baseValue: value,
        unitRatio: FEET_PER_MILE,
        smallUnitKey: 'common.units.feet' as TranslationKey,
        largeUnitKey: 'common.units.miles' as TranslationKey,
        opts,
        formatSmallValue: (val, precision) => {
          if (precision === 0) {
            return Math.round(val);
          }
          return parseFloat(val.toFixed(precision));
        },
        formatLargeValue: (val, precision) => {
          if (precision === 0) {
            // Round away from 0, so -0.5 rounds to -1 and 0.5 rounds to 1
            return Math.sign(val) * Math.round(Math.abs(val));
          }
          return parseFloat(val.toFixed(precision));
        },
      });
    }
    // Kilometers & Meters
    else {
      const kilometersValue = UnitConverter.convertMilesToKilometers(value, 'imperial', 'metric');
      return this._formatDistance({
        baseValue: kilometersValue,
        unitRatio: METERS_PER_KILOMETER,
        smallUnitKey: 'common.units.meters' as TranslationKey,
        largeUnitKey: 'common.units.kilometers' as TranslationKey,
        opts,
        formatSmallValue: (val, precision) => {
          const actualPrecision = precision === 0 ? 2 : precision;
          return parseFloat(val.toFixed(actualPrecision));
        },
        formatLargeValue: (val, precision) => {
          const actualPrecision = precision === 0 ? 2 : precision;
          return parseFloat(val.toFixed(actualPrecision));
        },
      });
    }
  }

  private _formatNumber(value: number, precision: number): string {
    if (precision === 0) {
      return this._numberPipe.transform(value);
    }
    // If the value is an integer, don't show trailing zeros
    const rounded = parseFloat(value.toFixed(precision));
    if (rounded % 1 === 0) {
      return this._numberPipe.transform(rounded);
    }
    // digitsInfo format: "{minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}"
    // This ensures the number is formatted with exactly `precision` decimal places.
    // Example: digitsInfo "1.2-2" means at least 1 integer digit and exactly 2 decimal places.
    // This is needed because FsNumberPipe (which wraps Angular's DecimalPipe) requires
    // this format string to control decimal precision, and we want to show exactly the
    // number of decimal places specified by the precision option.
    const digitsInfo = `1.${precision}-${precision}`;
    return this._numberPipe.transform(value, digitsInfo);
  }

  private _formatDistance({
    baseValue,
    unitRatio,
    smallUnitKey,
    largeUnitKey,
    opts,
    formatSmallValue,
    formatLargeValue,
  }: {
    baseValue: number;
    unitRatio: number;
    smallUnitKey: TranslationKey;
    largeUnitKey: TranslationKey;
    opts: Required<FsMilesToDistanceOptions>;
    formatSmallValue: (value: number, precision: number) => number;
    formatLargeValue: (value: number, precision: number) => number;
  }): string {
    // -1 > value < 1 mile/km - show feet/meters
    if (Math.abs(baseValue) < 1) {
      const smallValue = baseValue * unitRatio;
      const formattedNumeric = formatSmallValue(smallValue, opts.precision);
      const normalizedNumeric = Object.is(formattedNumeric, -0) ? 0 : formattedNumeric;
      if (!opts.includeUnit) {
        return this._formatNumber(normalizedNumeric, opts.precision);
      }

      const numericValue = normalizedNumeric === 0 ? 0 : parseFloat(normalizedNumeric.toString());
      const formattedNumber = this._formatNumber(normalizedNumeric, opts.precision);
      return this._translationService.translate(smallUnitKey, {
        value: formattedNumber,
        count: Math.abs(numericValue),
      });
    }
    // value <= -1 || 1 <= value - show miles/km
    else {
      const formattedNumeric = formatLargeValue(baseValue, opts.precision);
      const normalizedNumeric = Object.is(formattedNumeric, -0) ? 0 : formattedNumeric;
      if (!opts.includeUnit) {
        return this._formatNumber(normalizedNumeric, opts.precision);
      }

      const numericValue = normalizedNumeric === 0 ? 0 : parseFloat(normalizedNumeric.toString());
      const formattedNumber = this._formatNumber(normalizedNumeric, opts.precision);
      return this._translationService.translate(largeUnitKey, {
        value: formattedNumber,
        count: Math.abs(numericValue),
      });
    }
  }
}
